import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ORDERED_MISSION_LIFECYCLES } from '../../../constants.ts';
import { patchMissions } from '../../../queries/getMissions.ts';
import { IMission, IMissionPatch } from '../../../schema/Mission.ts';
import { LoadingButton } from '../../ui/Button.tsx';
import { SearchableSelect } from '../../ui/Select.tsx';

interface Props {
  show: boolean;
  onHide: () => void;
  mission: IMission;
  onUpdated: (lifecycle: string) => void;
}

export function EditMissionModal({ show, onHide, mission, onUpdated }: Readonly<Props>) {
  const { t } = useTranslation();
  const params = useParams() as { territorySlug: string };
  const [selectedLifecycle, setSelectedLifecycle] = useState<string>(mission.lifecycle);

  const mutation = useMutation({
    mutationFn: (payload: IMissionPatch) => patchMissions(params.territorySlug, mission.id, payload),
    onSuccess: (_data, variables) => {
      onUpdated(variables.lifecycle);
      toast.success(
        `${t('mission.mission')} ${mission.id} ${t('mission.new-lifecycle')} ${variables.lifecycle}`,
      );
      onHide();
    },
    onError: () => {
      toast.error(`${t('mission.mission')} ${mission.id} ${t('mission.failed-lifecycle')}`);
    },
  });

  const handleSubmit = () => {
    mutation.mutate({ lifecycle: selectedLifecycle });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('mission.mission')} #{mission.id}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SearchableSelect
          placeholder={t('mission.select-lifecycle')}
          options={ORDERED_MISSION_LIFECYCLES.map(lifecycle => ({
            value: lifecycle,
            label: lifecycle,
          }))}
          onChange={value => setSelectedLifecycle(value)}
          value={selectedLifecycle}
        />
      </Modal.Body>
      <Modal.Footer className={'border-0'}>
        <LoadingButton
          type={'submit-button'}
          text={t('mission.edit-submit')}
          imagePath={'/theme/media/pencil.svg'}
          loading={mutation.isPending}
          onClick={handleSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
}
